import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSettings } from "@/context/SettingsContext";
import { ImageUpload } from "@/components/ImageUpload";
import { TermListEditor } from "@/components/TermListEditor";
import { LaunchGateSettings } from "@/components/LaunchGateSettings";

export default function SettingsAdmin() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const { refresh } = useSettings();

  const syncNow = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/admin/sync-all"); toast.success(`Synced ${data.shows_synced} feeds · ${data.episodes_added} new episode${data.episodes_added === 1 ? "" : "s"}`); }
    catch (err) { toast.error(errorMessage(err)); }
    finally { setSyncing(false); }
  };

  const [defaults, setDefaults] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {}); api.get("/admin/banned-defaults").then((r) => setDefaults(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { const { data } = await api.put("/admin/settings", settings); setSettings(data); refresh(); toast.success("Settings saved"); }
    catch (err) { toast.error(errorMessage(err)); }
    finally { setSaving(false); }
  };

  if (!settings) return null;
  return (
    <div data-testid="admin-settings">
      <p className="eyebrow mb-2">Configuration</p>
      <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mb-8">Site Settings</h1>
      <form onSubmit={save} className="card-surface rounded-2xl p-6 space-y-5 max-w-2xl">
        <LaunchGateSettings settings={settings} set={set} />
        <div className="pt-4 border-t border-white/10">
          <Label>YouTube channel (URL, @handle or channel ID)</Label>
          <Input value={settings.youtube_channel} onChange={(e) => set("youtube_channel", e.target.value)} placeholder="https://www.youtube.com/@YourChannel" className="mt-1.5 bg-white/5 border-white/10" data-testid="settings-youtube-input" />
          <p className="mt-1.5 text-xs text-slate-500">Videos are pulled from the channel's public feed and refresh automatically every 10 minutes.{settings.youtube_channel_id && <> Resolved ID: <span className="font-mono text-sky-400" data-testid="settings-youtube-channel-id">{settings.youtube_channel_id}</span></>}</p>
        </div>
        <div><Label>Site tagline</Label><Input value={settings.site_tagline} onChange={(e) => set("site_tagline", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" data-testid="settings-tagline-input" /></div>
        <div>
          <Label>Audio feed sync interval</Label>
          <div className="mt-1.5 flex gap-2">
            <select value={settings.sync_interval_minutes || 60} onChange={(e) => set("sync_interval_minutes", Number(e.target.value))} className="h-10 flex-1 rounded-md bg-white/5 border border-white/10 px-3 text-sm" data-testid="settings-sync-interval-select">
              {[[30, "Every 30 minutes"], [60, "Every 60 minutes"], [120, "Every 2 hours"]].map(([v, l]) => <option key={v} value={v} className="bg-[#0B1A33]">{l}</option>)}
            </select>
            <Button type="button" variant="outline" disabled={syncing} onClick={syncNow} className="h-10 rounded-md border-white/15 bg-transparent hover:bg-white/10 shrink-0" data-testid="settings-sync-now-button"><RefreshCw size={14} className={`mr-2 ${syncing ? "animate-spin" : ""}`} />{syncing ? "Syncing…" : "Sync feeds now"}</Button>
          </div>
          <p className="mt-1.5 text-xs text-slate-500">How often RSS feeds are checked for new episodes. YouTube & TikTok always refresh every 4 hours. Takes effect after the current wait finishes.</p>
        </div>
        <div>
          <Label>Site logo <span className="text-slate-500">(remove to use the built-in badge)</span></Label>
          <div className="mt-2 flex justify-start"><ImageUpload value={settings.logo_url} onChange={(v) => set("logo_url", v)} testId="settings-logo" /></div>
        </div>
        <div className="pt-4 border-t border-white/10">
          <p className="eyebrow mb-3">Social & listening links</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {[["instagram_url", "Instagram"], ["twitter_url", "X / Twitter"], ["facebook_url", "Facebook"], ["tiktok_url", "TikTok"], ["spotify_url", "Spotify"], ["apple_podcasts_url", "Apple Podcasts"]].map(([key, label]) => (
              <div key={key}><Label>{label}</Label><Input value={settings[key] || ""} onChange={(e) => set(key, e.target.value)} placeholder="https://…" className="mt-1.5 bg-white/5 border-white/10" data-testid={`settings-${key.replace("_url", "")}-input`} /></div>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-500">Links appear in the header and footer. Leave blank to hide.</p>
        </div>
        <div className="pt-4 border-t border-white/10 space-y-5">
          <div><p className="eyebrow mb-1">Host publishing rules</p><p className="text-xs text-slate-500">Hosts can't publish an episode whose title or summary contains any of these terms, and the Summary Tool avoids them when drafting. Matching is case-insensitive on whole words.</p></div>
          <TermListEditor label="Blocked sportsbook names" hint="Brands that must never appear in a summary." value={settings.banned_sportsbooks || []} onChange={(v) => set("banned_sportsbooks", v)} defaults={defaults?.sportsbooks} testId="settings-sportsbooks" tone="amber" />
          <TermListEditor label="Blocked profanity" hint="Hard curse words and slurs that block publishing." value={settings.banned_profanity || []} onChange={(v) => set("banned_profanity", v)} defaults={defaults?.profanity} testId="settings-profanity" tone="rose" />
        </div>
        <Button type="submit" disabled={saving} className="rounded-full bg-sky-400 text-[#070F1E] font-bold hover:bg-sky-300" data-testid="settings-save-button">{saving ? "Saving…" : "Save settings"}</Button>
      </form>
      <ChangePassword />
    </div>
  );
}

function ChangePassword() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) return toast.error("New password must be at least 8 characters");
    if (form.new_password !== form.confirm) return toast.error("New passwords don't match");
    setBusy(true);
    try { await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password }); toast.success("Password updated — use it next time you sign in"); setForm({ current_password: "", new_password: "", confirm: "" }); }
    catch (err) { toast.error(errorMessage(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="card-surface rounded-2xl p-6 space-y-4 max-w-2xl mt-6" data-testid="change-password-form">
      <div><p className="eyebrow mb-1">Security</p><h2 className="font-display text-xl font-bold">Change admin password</h2><p className="text-xs text-slate-500 mt-1">Minimum 8 characters. You'll stay signed in on this device.</p></div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div><Label>Current password</Label><Input type="password" required autoComplete="current-password" value={form.current_password} onChange={(e) => set("current_password", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" data-testid="password-current-input" /></div>
        <div><Label>New password</Label><Input type="password" required minLength={8} autoComplete="new-password" value={form.new_password} onChange={(e) => set("new_password", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" data-testid="password-new-input" /></div>
        <div><Label>Confirm new password</Label><Input type="password" required autoComplete="new-password" value={form.confirm} onChange={(e) => set("confirm", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" data-testid="password-confirm-input" /></div>
      </div>
      <Button type="submit" disabled={busy} variant="outline" className="rounded-full border-white/15 bg-transparent hover:bg-white/10" data-testid="password-submit-button">{busy ? "Updating…" : "Update password"}</Button>
    </form>
  );
}
